import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Upload,
  X as XIcon,
  FileText,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  FolderPlus,
  Ungroup,
  Pencil,
  Check,
} from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import {
  BULK_UPLOAD_LIMITS,
  type UploadPipeline,
} from '../../redux/api/documentsApi';
import { mergePdfs, type MergeProgress } from '../../utils/mergePdfs';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FileEntry {
  id: string;
  file: File;
}

interface FileGroup {
  id: string;
  name: string;
  fileIds: string[];
}

interface GroupMergeProgress {
  [groupId: string]: MergeProgress;
}

type ModalPhase = 'dropzone' | 'analyzing' | 'manage' | 'merging';

export type DocumentType = 'asbuilt' | 'event_log';

interface UploadAsBuiltModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: Array<{ file: File; runExtraction: boolean; documentType: DocumentType }>) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

let fileIdCounter = 0;
function nextFileId(): string {
  return `f-${++fileIdCounter}-${Date.now()}`;
}

let groupIdCounter = 0;
function nextGroupId(): string {
  return `g-${++groupIdCounter}-${Date.now()}`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

const UploadAsBuiltModal: React.FC<UploadAsBuiltModalProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [groups, setGroups] = useState<FileGroup[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<ModalPhase>('dropzone');
  const [error, setError] = useState<string | null>(null);
  const [mergeProgress, setMergeProgress] = useState<GroupMergeProgress>({});
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pageCounts, setPageCounts] = useState<Map<string, number>>(new Map());
  const [extractionMap, setExtractionMap] = useState<Map<string, boolean>>(new Map());
  const [drawingQaMap, setDrawingQaMap] = useState<Map<string, boolean>>(new Map());
  const [documentTypeMap, setDocumentTypeMap] = useState<Map<string, DocumentType>>(new Map());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const countingRef = useRef<Set<string>>(new Set());
  const userToggledIds = useRef<Set<string>>(new Set());

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setGroups([]);
      setSelectedIds(new Set());
      setPhase('dropzone');
      setError(null);
      setMergeProgress({});
      setMergeError(null);
      setEditingGroupId(null);
      setExpandedGroups(new Set());
      setIsDragging(false);
      setShowConfirm(false);
      setPageCounts(new Map());
      setExtractionMap(new Map());
      setDocumentTypeMap(new Map());
      countingRef.current = new Set();
      userToggledIds.current = new Set();
    }
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ─── Derived state ───────────────────────────────────────────────────────

  const groupedFileIds = useMemo(() => {
    const ids = new Set<string>();
    groups.forEach((g) => g.fileIds.forEach((id) => ids.add(id)));
    return ids;
  }, [groups]);

  const ungroupedFiles = useMemo(
    () => files.filter((f) => !groupedFileIds.has(f.id)),
    [files, groupedFileIds],
  );

  const totalFileCount = files.length;

  const uploadFileCount = useMemo(() => {
    const groupCount = groups.filter((g) => g.fileIds.length > 0).length;
    return ungroupedFiles.length + groupCount;
  }, [ungroupedFiles, groups]);

  const analyzedCount = useMemo(
    () => files.filter((f) => pageCounts.has(f.id)).length,
    [files, pageCounts],
  );
  const allFilesCounted = files.length > 0 && analyzedCount === files.length;

  // ─── Page counting ─────────────────────────────────────────────────────

  useEffect(() => {
    for (const entry of files) {
      if (countingRef.current.has(entry.id)) continue;
      countingRef.current.add(entry.id);

      (async () => {
        try {
          const arrayBuffer = await entry.file.arrayBuffer();
          const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
          setPageCounts((prev) => new Map(prev).set(entry.id, pdf.getPageCount()));
        } catch {
          setPageCounts((prev) => new Map(prev).set(entry.id, 0));
        }
      })();
    }
  }, [files]);

  // Transition from analyzing → manage once all page counts are in
  useEffect(() => {
    if (phase === 'analyzing' && allFilesCounted) {
      setPhase('manage');
    }
  }, [phase, allFilesCounted]);

  // Per-site page counts: file ID for ungrouped, group ID for groups
  const sitePageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of ungroupedFiles) {
      counts.set(entry.id, pageCounts.get(entry.id) ?? 0);
    }
    for (const group of groups) {
      let total = 0;
      for (const fileId of group.fileIds) {
        total += pageCounts.get(fileId) ?? 0;
      }
      counts.set(group.id, total);
    }
    return counts;
  }, [ungroupedFiles, groups, pageCounts]);

  // Auto-set extraction default per site based on its page count (unless user toggled it)
  useEffect(() => {
    setExtractionMap((prev) => {
      const next = new Map(prev);
      sitePageCounts.forEach((pageCount, siteId) => {
        if (!userToggledIds.current.has(siteId) || !next.has(siteId)) {
          next.set(siteId, pageCount < 70);
        }
      });
      return next;
    });
  }, [sitePageCounts]);

  const handleDocumentTypeChange = useCallback((siteId: string, docType: DocumentType) => {
    setDocumentTypeMap((prev) => {
      const next = new Map(prev);
      next.set(siteId, docType);
      return next;
    });
  }, []);

  const handleToggleExtraction = useCallback((siteId: string) => {
    userToggledIds.current.add(siteId);
    setExtractionMap((prev) => {
      const next = new Map(prev);
      next.set(siteId, !(prev.get(siteId) ?? true));
      return next;
    });
  }, []);

  const handleToggleDrawingQa = useCallback((siteId: string) => {
    setDrawingQaMap((prev) => {
      const next = new Map(prev);
      next.set(siteId, !(prev.get(siteId) ?? true));
      return next;
    });
  }, []);

  // ─── Validation ──────────────────────────────────────────────────────────

  const validateFiles = useCallback(
    (newFiles: File[]): string | null => {
      for (const file of newFiles) {
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
          return `"${file.name}" is not a PDF file`;
        }
      }

      const allFiles = [...files.map((f) => f.file), ...newFiles];
      if (allFiles.length > BULK_UPLOAD_LIMITS.MAX_FILES) {
        return `Maximum ${BULK_UPLOAD_LIMITS.MAX_FILES} files allowed`;
      }

      const maxFileSize = BULK_UPLOAD_LIMITS.MAX_FILE_SIZE_MB * 1024 * 1024;
      const maxTotalSize = BULK_UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB * 1024 * 1024;
      let totalSize = 0;

      for (const file of allFiles) {
        if (file.size > maxFileSize) {
          return `"${file.name}" exceeds ${BULK_UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB limit`;
        }
        totalSize += file.size;
      }

      if (totalSize > maxTotalSize) {
        return `Total size exceeds ${BULK_UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB}MB limit`;
      }

      return null;
    },
    [files],
  );

  // ─── File adding ─────────────────────────────────────────────────────────

  const addFiles = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length === 0) return;

      const validationError = validateFiles(newFiles);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      const entries: FileEntry[] = newFiles.map((file) => ({
        id: nextFileId(),
        file,
      }));
      setFiles((prev) => [...prev, ...entries]);
      if (phase === 'dropzone') {
        setPhase('analyzing');
      }
    },
    [validateFiles, phase],
  );

  // ─── Drag and drop ───────────────────────────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files).filter(
        (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
      );
      addFiles(droppedFiles);
    },
    [addFiles],
  );

  const handleBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      addFiles(selectedFiles);
      // Reset so same files can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles],
  );

  // ─── File management ─────────────────────────────────────────────────────

  const removeFile = useCallback(
    (fileId: string) => {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      setGroups((prev) =>
        prev.map((g) => ({
          ...g,
          fileIds: g.fileIds.filter((id) => id !== fileId),
        })),
      );
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    },
    [],
  );

  const toggleSelect = useCallback((fileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const selectAllUngrouped = useCallback(() => {
    if (ungroupedFiles.length === 0) return;
    const allSelected = ungroupedFiles.every((f) => selectedIds.has(f.id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ungroupedFiles.forEach((f) => next.delete(f.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ungroupedFiles.forEach((f) => next.add(f.id));
        return next;
      });
    }
  }, [ungroupedFiles, selectedIds]);

  // ─── Grouping ────────────────────────────────────────────────────────────

  const createGroupFromSelected = useCallback(() => {
    const idsToGroup = Array.from(selectedIds).filter(
      (id) => !groupedFileIds.has(id) && files.some((f) => f.id === id),
    );
    if (idsToGroup.length < 2) return;

    const groupId = nextGroupId();
    const firstFile = files.find((f) => f.id === idsToGroup[0]);
    const baseName = firstFile
      ? firstFile.file.name.replace(/\.pdf$/i, '')
      : 'Merged';

    setGroups((prev) => [
      ...prev,
      { id: groupId, name: `${baseName} (merged)`, fileIds: idsToGroup },
    ]);
    setSelectedIds(new Set());
    setExpandedGroups((prev) => new Set([...prev, groupId]));
  }, [selectedIds, groupedFileIds, files]);

  const ungroupFile = useCallback((groupId: string, fileId: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, fileIds: g.fileIds.filter((id) => id !== fileId) }
          : g,
      ),
    );
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const startEditGroupName = useCallback(
    (groupId: string, currentName: string) => {
      setEditingGroupId(groupId);
      setEditingGroupName(currentName);
    },
    [],
  );

  const saveGroupName = useCallback(() => {
    if (editingGroupId && editingGroupName.trim()) {
      setGroups((prev) =>
        prev.map((g) =>
          g.id === editingGroupId ? { ...g, name: editingGroupName.trim() } : g,
        ),
      );
    }
    setEditingGroupId(null);
    setEditingGroupName('');
  }, [editingGroupId, editingGroupName]);

  const toggleGroupExpanded = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // ─── Merge & Upload ──────────────────────────────────────────────────────

  const handleMergeAndUpload = useCallback(async () => {
    setShowConfirm(false);
    setPhase('merging');
    setMergeError(null);
    setMergeProgress({});

    const fileMap = new Map(files.map((f) => [f.id, f.file]));
    const finalFiles: Array<{ file: File; runExtraction: boolean; documentType: DocumentType }> = [];

    // Add ungrouped files as-is
    for (const entry of ungroupedFiles) {
      const docType = documentTypeMap.get(entry.id) ?? 'asbuilt';
      finalFiles.push({
        file: entry.file,
        runExtraction: docType === 'asbuilt' ? (extractionMap.get(entry.id) ?? true) : false,
        documentType: docType,
      });
    }

    // Merge each group
    const activeGroups = groups.filter((g) => g.fileIds.length > 0);
    for (const group of activeGroups) {
      const groupFiles = group.fileIds
        .map((id) => fileMap.get(id))
        .filter((f): f is File => f !== undefined);

      if (groupFiles.length === 0) continue;

      try {
        const outputName = group.name.endsWith('.pdf')
          ? group.name
          : `${group.name}.pdf`;

        const merged = await mergePdfs(groupFiles, outputName, (progress) => {
          setMergeProgress((prev) => ({ ...prev, [group.id]: progress }));
        });
        const groupDocType = documentTypeMap.get(group.id) ?? 'asbuilt';
        finalFiles.push({
          file: merged,
          runExtraction: groupDocType === 'asbuilt' ? (extractionMap.get(group.id) ?? true) : false,
          documentType: groupDocType,
        });
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Unknown error during merge';
        setMergeError(`Group "${group.name}": ${msg}`);
        setPhase('manage');
        return;
      }
    }

    // Validate final count
    if (finalFiles.length > BULK_UPLOAD_LIMITS.MAX_FILES) {
      setMergeError(
        `Too many files after merge (${finalFiles.length}). Maximum is ${BULK_UPLOAD_LIMITS.MAX_FILES}.`,
      );
      setPhase('manage');
      return;
    }

    onUpload(finalFiles);
    onClose();
  }, [files, ungroupedFiles, groups, onUpload, onClose, extractionMap, drawingQaMap]);

  const hasAnySiteWithNoPipeline = useMemo(() => {
    for (const entry of ungroupedFiles) {
      const extraction = extractionMap.get(entry.id) ?? true;
      const drawingQa = drawingQaMap.get(entry.id) ?? true;
      if (!extraction && !drawingQa) return true;
    }
    for (const group of groups) {
      if (group.fileIds.length === 0) continue;
      const extraction = extractionMap.get(group.id) ?? true;
      const drawingQa = drawingQaMap.get(group.id) ?? true;
      if (!extraction && !drawingQa) return true;
    }
    return false;
  }, [ungroupedFiles, groups, extractionMap, drawingQaMap]);

  const handleConfirmClick = useCallback(() => {
    setShowConfirm(true);
  }, []);

  // ─── Check if back to empty ──────────────────────────────────────────────

  useEffect(() => {
    if (files.length === 0 && (phase === 'manage' || phase === 'analyzing')) {
      setPhase('dropzone');
      setGroups([]);
      setSelectedIds(new Set());
    }
  }, [files.length, phase]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const selectedUngroupedCount = ungroupedFiles.filter((f) =>
    selectedIds.has(f.id),
  ).length;

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[var(--color-modal-backdrop)] backdrop-blur-sm"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && phase !== 'merging') {
          onClose();
        }
      }}
    >
      <div
        className="bg-[var(--color-modal-bg)] border border-[var(--color-border)] rounded-lg w-full max-w-2xl mx-4 relative flex flex-col shadow-xl"
        style={{
          zIndex: 100000,
          maxHeight: '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)] flex-shrink-0">
          <h3 className="text-lg font-bold text-[var(--color-text-primary)] flex items-center gap-2">
            <Upload className="w-5 h-5 text-engramiq-green" />
            Upload Documents
          </h3>
          {phase !== 'merging' && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-[var(--color-surface-hover)] rounded text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
              <span className="text-sm text-accent-red">{error}</span>
            </div>
          )}

          {mergeError && (
            <div className="mb-4 p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
              <span className="text-sm text-accent-red">{mergeError}</span>
            </div>
          )}

          {/* Phase 1: Full dropzone */}
          {phase === 'dropzone' && (
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-engramiq-green bg-engramiq-green/10'
                  : 'border-engramiq-green/30 hover:border-engramiq-green/60'
              }`}
              onClick={handleBrowse}
            >
              <Upload className="w-12 h-12 text-[var(--color-text-secondary)] mx-auto mb-4" />
              <p className="text-[var(--color-text-primary)] text-lg mb-2">
                Drag & drop PDF files here
              </p>
              <p className="text-[var(--color-text-secondary)] text-sm mb-4">
                or click to browse
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowse();
                }}
                className="px-4 py-2 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/40 rounded-lg text-sm text-engramiq-green transition-all"
              >
                Browse Files
              </button>
              <p className="text-[var(--color-text-muted)] text-xs mt-4">
                PDF files only &middot; Max {BULK_UPLOAD_LIMITS.MAX_FILES} files &middot; {BULK_UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB per file
              </p>
            </div>
          )}

          {/* Phase 1.5: Analyzing files */}
          {phase === 'analyzing' && (
            <div className="py-10 text-center">
              <Loader2 className="w-8 h-8 text-engramiq-green animate-spin mx-auto mb-3" />
              <p className="text-[var(--color-text-primary)] font-medium mb-1">
                Analyzing files...
              </p>
              <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                Reading page counts to set extraction defaults
              </p>
              <div className="w-48 mx-auto bg-[var(--color-overlay-medium)] rounded-full h-1.5">
                <div
                  className="bg-engramiq-green h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${files.length > 0 ? (analyzedCount / files.length) * 100 : 0}%` }}
                />
              </div>
              <p className="text-xs text-[var(--color-text-muted)] mt-2">
                {analyzedCount} of {files.length} file{files.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Phase 2: File management & grouping */}
          {phase === 'manage' && (
            <>
              {/* Compact drop zone bar */}
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border border-dashed rounded-lg p-3 mb-4 flex items-center justify-between transition-all ${
                  isDragging
                    ? 'border-engramiq-green bg-engramiq-green/10'
                    : 'border-engramiq-green/30 hover:border-engramiq-green/50'
                }`}
              >
                <span className="text-sm text-[var(--color-text-secondary)]">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Drop more files or{' '}
                  <button
                    type="button"
                    onClick={handleBrowse}
                    className="text-engramiq-green hover:underline"
                  >
                    browse
                  </button>
                </span>
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {totalFileCount} file{totalFileCount !== 1 ? 's' : ''} added
                </span>
              </div>

              {/* Instructions */}
              <p className="text-xs text-[var(--color-text-secondary)] mb-3">
                Group files from the same site using the selectors below. A single PDF per site can be uploaded ungrouped. <span className="text-[var(--color-text-muted)]">Note: node-level extraction is only available for sites under 30 MW.</span>
              </p>

              {/* Individual Sites (ungrouped files) */}
              {ungroupedFiles.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                      Individual Sites ({ungroupedFiles.length})
                    </h4>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllUngrouped}
                        className="text-xs text-engramiq-green hover:underline"
                      >
                        {ungroupedFiles.every((f) => selectedIds.has(f.id))
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                      <div className="relative group/tip">
                        <button
                          type="button"
                          onClick={createGroupFromSelected}
                          disabled={selectedUngroupedCount < 2}
                          className="px-3 py-1 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/40 rounded text-xs text-engramiq-green transition-all flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-engramiq-green/20"
                        >
                          <FolderPlus className="w-3.5 h-3.5" />
                          {selectedUngroupedCount >= 2
                            ? `Group Selected (${selectedUngroupedCount})`
                            : 'Group Selected'}
                        </button>
                        {selectedUngroupedCount < 2 && (
                          <span className="absolute bottom-full right-0 mb-1.5 px-2 py-1 bg-[var(--color-sidebar-tooltip-bg)] border border-[var(--color-border)] rounded text-[10px] text-[var(--color-sidebar-tooltip-text)] whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none">
                            Select 2 or more files to group
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[var(--color-text-secondary)] mb-1">
                    Select files from the same site to group
                  </p>
                  {/* Column headers */}
                  <div className="flex items-center gap-3 px-2 mb-1">
                    <div className="w-4 flex-shrink-0" />
                    <div className="w-4 flex-shrink-0" />
                    <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] flex-1">File</span>
                    <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] w-20 flex-shrink-0 text-center">Type</span>
                    <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] flex-shrink-0">Size</span>
                    <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] w-9 flex-shrink-0 text-center" title="Node-level extraction — only available for sites under 30 MW">Extract</span>
                    <div className="w-7 flex-shrink-0" />
                  </div>
                  <div className="space-y-1">
                    {ungroupedFiles.map((entry) => {
                      const sitePages = sitePageCounts.get(entry.id) ?? 0;
                      const extraction = extractionMap.get(entry.id) ?? true;
                      const docType = documentTypeMap.get(entry.id) ?? 'asbuilt';
                      return (
                        <div key={entry.id}>
                          <div
                            className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                              selectedIds.has(entry.id)
                                ? 'bg-engramiq-green/10 border border-engramiq-green/30'
                                : 'bg-[var(--color-surface)] border border-transparent hover:border-engramiq-green/20'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelect(entry.id)}
                              className="w-4 h-4 rounded border-engramiq-green/40 bg-transparent text-engramiq-green focus:ring-engramiq-green cursor-pointer accent-[#17c480]"
                            />
                            <FileText className="w-4 h-4 text-[var(--color-text-secondary)] flex-shrink-0" />
                            <span className="text-sm text-[var(--color-text-primary)] truncate flex-1">
                              {entry.file.name}
                            </span>
                            <select
                              value={docType}
                              onChange={(e) => handleDocumentTypeChange(entry.id, e.target.value as DocumentType)}
                              className="w-20 h-6 px-1 text-[10px] rounded border border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] flex-shrink-0"
                            >
                              <option value="asbuilt">As-Built</option>
                              <option value="event_log">Event Log</option>
                            </select>
                            <span className="text-xs text-[var(--color-text-secondary)] flex-shrink-0">
                              {formatBytes(entry.file.size)}
                              {sitePages > 0 && ` · ${sitePages}p`}
                            </span>
                            {docType === 'asbuilt' ? (
                              <button
                                type="button"
                                role="switch"
                                aria-checked={extraction}
                                onClick={(e) => { e.stopPropagation(); handleToggleExtraction(entry.id); }}
                                title={extraction ? 'Extraction enabled' : 'Extraction disabled'}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                                  extraction ? 'bg-engramiq-green' : 'bg-[var(--color-overlay-medium)]'
                                }`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                    extraction ? 'translate-x-[18px]' : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                            ) : (
                              <span className="w-9 flex-shrink-0 text-center text-[10px] text-[var(--color-text-muted)]">auto</span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeFile(entry.id)}
                              className="p-1 hover:bg-accent-red/20 rounded text-[var(--color-text-secondary)] hover:text-accent-red transition-colors flex-shrink-0"
                              title="Remove file"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {docType === 'asbuilt' && extraction && sitePages >= 70 && (
                            <div className="ml-7 mt-1 mb-1 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              <span className="text-[11px] text-amber-500">
                                Node-level extraction is not currently available for sites &gt;30 MW.
                              </span>
                            </div>
                          )}
                          {docType === 'asbuilt' && !extraction && (
                            <p className="ml-7 mt-0.5 mb-1 text-[11px] text-[var(--color-text-muted)]">
                              Node-level extraction off — drawing quality analysis available after upload
                            </p>
                          )}
                          {docType === 'event_log' && (
                            <p className="ml-7 mt-0.5 mb-1 text-[11px] text-[#17c480]">
                              Events will be automatically extracted after upload
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Merged Sites (groups) — always visible */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Merged Sites ({groups.length})
                  </h4>
                  {groups.length > 0 && (
                    <div className="flex items-center gap-4">
                      <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]" title="Node-level extraction — only available for sites under 30 MW">Extract components</span>
                      <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]" title="Drawing quality analysis">Drawing QA</span>
                    </div>
                  )}
                </div>
                {groups.length === 0 ? (
                  <div className="border border-dashed border-[var(--color-border)] rounded-lg p-4 text-center">
                    <p className="text-xs text-[var(--color-text-secondary)]">
                      No groups yet — select files above and click Group Selected to merge multiple PDFs into one site.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {groups.map((group) => {
                      const groupFiles = group.fileIds
                        .map((id) => files.find((f) => f.id === id))
                        .filter((f): f is FileEntry => f !== undefined);
                      const groupSize = groupFiles.reduce(
                        (sum, f) => sum + f.file.size,
                        0,
                      );
                      const isExpanded = expandedGroups.has(group.id);
                      const groupPageCount = sitePageCounts.get(group.id) ?? 0;
                      const groupExtraction = extractionMap.get(group.id) ?? true;
                      const groupDrawingQa = drawingQaMap.get(group.id) ?? true;

                      return (
                        <div
                          key={group.id}
                          className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-surface)]"
                        >
                          {/* Group header */}
                          <div className="flex items-center gap-2 p-3 bg-engramiq-green/5">
                            <button
                              type="button"
                              onClick={() => toggleGroupExpanded(group.id)}
                              className="p-0.5 hover:bg-[var(--color-surface-hover)] rounded text-[var(--color-text-secondary)]"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            <FolderPlus className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                            {editingGroupId === group.id ? (
                              <div className="flex items-center gap-1 flex-1">
                                <input
                                  type="text"
                                  value={editingGroupName}
                                  onChange={(e) =>
                                    setEditingGroupName(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveGroupName();
                                    if (e.key === 'Escape') {
                                      setEditingGroupId(null);
                                      setEditingGroupName('');
                                    }
                                  }}
                                  autoFocus
                                  className="flex-1 px-2 py-0.5 bg-[var(--color-input-bg)] border border-engramiq-green/40 rounded text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-engramiq-green"
                                />
                                <button
                                  type="button"
                                  onClick={saveGroupName}
                                  className="p-1 hover:bg-[var(--color-surface-hover)] rounded text-engramiq-green"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span
                                className="text-sm font-medium text-[var(--color-text-primary)] truncate flex-1 cursor-pointer hover:text-engramiq-green"
                                onClick={() =>
                                  startEditGroupName(group.id, group.name)
                                }
                                title="Click to rename"
                              >
                                {group.name}
                                <Pencil className="w-3 h-3 inline ml-1.5 opacity-50" />
                              </span>
                            )}
                            <span className="text-xs text-[var(--color-text-secondary)] flex-shrink-0 min-w-[4rem]">
                              {groupFiles.length} file{groupFiles.length !== 1 ? 's' : ''} &middot;{' '}
                              {formatBytes(groupSize)}
                              {groupPageCount > 0 && ` · ${groupPageCount}p`}
                            </span>
                            <div className="w-24 flex-shrink-0 flex justify-center">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={groupExtraction}
                                onClick={() => handleToggleExtraction(group.id)}
                                title={groupExtraction ? 'Extraction enabled' : 'Extraction disabled'}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                                  groupExtraction ? 'bg-engramiq-green' : 'bg-[var(--color-overlay-medium)]'
                                }`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                    groupExtraction ? 'translate-x-[18px]' : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                            </div>
                            <div className="w-20 flex-shrink-0 flex justify-center">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={groupDrawingQa}
                                onClick={() => handleToggleDrawingQa(group.id)}
                                title={groupDrawingQa ? 'Drawing QA enabled' : 'Drawing QA disabled'}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                                  groupDrawingQa ? 'bg-engramiq-green' : 'bg-[var(--color-overlay-medium)]'
                                }`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                    groupDrawingQa ? 'translate-x-[18px]' : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                            </div>
                            <div className="w-7 flex-shrink-0 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeGroup(group.id)}
                                className="p-1 hover:bg-accent-red/20 rounded text-[var(--color-text-secondary)] hover:text-accent-red transition-colors"
                                title="Dissolve group"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          {groupExtraction && groupPageCount >= 70 && (
                            <div className="mx-3 my-2 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              <span className="text-[11px] text-amber-500">
                                Node-level extraction is not currently available for sites &gt;30 MW.
                              </span>
                            </div>
                          )}
                          {!groupExtraction && !groupDrawingQa && (
                            <div className="mx-3 my-2 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              <span className="text-[11px] text-amber-500">
                                Select at least one pipeline to proceed
                              </span>
                            </div>
                          )}
                          {!groupExtraction && groupDrawingQa && (
                            <p className="mx-3 my-1 text-[11px] text-[var(--color-text-muted)]">
                              Node-level extraction off — drawing quality analysis available after upload
                            </p>
                          )}

                          {/* Group files */}
                          {isExpanded && (
                            <div className="p-2 space-y-1">
                              {groupFiles.length === 0 ? (
                                <p className="text-xs text-[var(--color-text-secondary)] text-center py-2">
                                  No files in this group
                                </p>
                              ) : (
                                groupFiles.map((entry) => (
                                  <div
                                    key={entry.id}
                                    className="flex items-center gap-3 p-2 rounded bg-[var(--color-surface-hover)]"
                                  >
                                    <FileText className="w-4 h-4 text-[var(--color-text-secondary)] flex-shrink-0" />
                                    <span className="text-sm text-[var(--color-text-primary)] truncate flex-1">
                                      {entry.file.name}
                                    </span>
                                    <span className="text-xs text-[var(--color-text-secondary)] flex-shrink-0">
                                      {formatBytes(entry.file.size)}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        ungroupFile(group.id, entry.id)
                                      }
                                      className="p-1 hover:bg-[var(--color-surface-hover)] rounded text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors flex-shrink-0"
                                      title="Remove from group"
                                    >
                                      <Ungroup className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(entry.id)}
                                      className="p-1 hover:bg-accent-red/20 rounded text-[var(--color-text-secondary)] hover:text-accent-red transition-colors flex-shrink-0"
                                      title="Delete file"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Phase 3: Merging progress */}
          {phase === 'merging' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <Loader2 className="w-8 h-8 text-engramiq-green animate-spin mx-auto mb-2" />
                <p className="text-[var(--color-text-primary)]">
                  Merging and preparing files for upload...
                </p>
              </div>

              {groups
                .filter((g) => g.fileIds.length > 0)
                .map((group) => {
                  const progress = mergeProgress[group.id];
                  const percent = progress?.percent ?? 0;

                  return (
                    <div key={group.id} className="bg-[var(--color-surface)] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[var(--color-text-primary)] truncate">
                          {group.name}
                        </span>
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          {percent}%
                        </span>
                      </div>
                      <div className="w-full bg-[var(--color-overlay-medium)] rounded-full h-2">
                        <div
                          className="bg-engramiq-green h-2 rounded-full transition-all duration-300"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      {progress?.phase && (
                        <p className="text-xs text-[var(--color-text-secondary)] mt-1 capitalize">
                          {progress.phase}...
                        </p>
                      )}
                    </div>
                  );
                })}

              {ungroupedFiles.length > 0 && (
                <p className="text-xs text-[var(--color-text-secondary)] text-center">
                  + {ungroupedFiles.length} ungrouped file{ungroupedFiles.length !== 1 ? 's' : ''} ready
                </p>
              )}
            </div>
          )}
        </div>

        {/* Billing confirmation overlay */}
        {showConfirm && (
          <div className="absolute inset-0 bg-[var(--color-modal-backdrop)] backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
            <div className="bg-[var(--color-modal-bg)] border border-[var(--color-border)] rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[var(--color-overlay-medium)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-[var(--color-text-primary)]" />
                </div>
                <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
                  Ready to process this upload?
                </h2>
              </div>
              <p className="text-sm text-[var(--color-text-primary)] leading-relaxed mb-6">
                We're about to start processing {uploadFileCount} file{uploadFileCount !== 1 ? 's' : ''}.
                {groups.filter((g) => g.fileIds.length > 0).length > 0 && (
                  <> Grouped files will be merged into single PDFs before upload.</>
                )}
                <br />
                Once you accept, the upload can't be undone, and processing
                charges will be billed to your Engramiq account.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/40 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleMergeAndUpload}
                  className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq"
                >
                  Accept & process
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {phase === 'manage' && (
          <div className="flex items-center justify-between p-5 border-t border-[var(--color-border)] flex-shrink-0">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {uploadFileCount} {uploadFileCount === 1 ? 'site' : 'sites'}
              {(ungroupedFiles.length > 0 || groups.filter((g) => g.fileIds.length > 0).length > 0) && (
                <> ({groups.filter((g) => g.fileIds.length > 0).length} merged, {ungroupedFiles.length} individual)</>
              )}
            </span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/40 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={uploadFileCount === 0 || hasAnySiteWithNoPipeline}
                className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {groups.some((g) => g.fileIds.length > 0)
                  ? `Merge & Upload (${uploadFileCount} ${uploadFileCount === 1 ? 'site' : 'sites'})`
                  : `Upload (${uploadFileCount} ${uploadFileCount === 1 ? 'site' : 'sites'})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(modalContent, portalTarget);
};

export default UploadAsBuiltModal;
